import { concat, fn } from '@ember/helper';
import { get } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type DetailedChangeset } from 'ember-changeset/types';
import PowerSelect from 'ember-power-select/components/power-select';
import { or } from 'ember-truth-helpers';
import SensitiveRevealToggle from 'garaje/components/entries/sensitive-reveal-toggle';
import SensitiveFieldShield from 'garaje/components/sensitive-field-shield';
import isRemovedString from 'garaje/helpers/is-removed-string';
import removedString from 'garaje/helpers/removed-string';
import type EmployeeModel from 'garaje/models/employee';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import italicizeIfRemoved from 'garaje/modifiers/italicize-if-removed';
import { type SensitiveState } from 'garaje/services/sensitive-fields-visibility';
import { firstLowercase } from 'garaje/utils/first-lowercase';
import { empty, not, and } from 'macro-decorators';
import { cached } from 'tracked-toolbox';

interface EntriesHostFieldSignature {
  Args: {
    allowClear?: boolean;
    changeset: DetailedChangeset<SignInFieldModel> | SignInFieldModel;
    disabled?: boolean;
    errors?: string[] | string;
    label: string;
    required?: boolean;
    sensitiveState?: SensitiveState;
    selected: Partial<EmployeeModel> | null;
    search: (term: string) => Promise<EmployeeModel[]>;
    update: (model: DetailedChangeset<SignInFieldModel> | SignInFieldModel, selected: EmployeeModel) => unknown;
    validated?: boolean;
  };
  Element: HTMLDivElement;
}

export default class EntriesHostField extends Component<EntriesHostFieldSignature> {
  @empty('args.errors') declare hasNoErrors: boolean;
  @not('hasNoErrors') declare hasErrors: boolean;
  @and('args.validated', 'hasErrors') declare isDisplayingErrors: boolean;

  @tracked isRevealed = false;

  get isSensitive(): boolean {
    return Boolean(this.args.sensitiveState);
  }

  get isRevealable(): boolean {
    return this.args.sensitiveState === 'visible' && Boolean(this.args.selected);
  }

  get isMasked(): boolean {
    if (this.args.sensitiveState === 'hidden') {
      return true;
    }
    return this.isRevealable && !this.isRevealed;
  }

  toggleRevealed = (): void => {
    this.isRevealed = !this.isRevealed;
  };

  @cached
  get selected(): Partial<EmployeeModel> | null {
    const { selected } = this.args;
    // eslint-disable-next-line ember/no-get
    if (isRemovedString(get(selected ?? {}, 'name'))) {
      // eslint-disable-next-line ember/no-get
      return { name: removedString(get(selected ?? {}, 'name')) };
    }
    return selected;
  }

  <template>
    <div data-test-flow-field={{@changeset.name}} data-test-custom-field={{@changeset.name}} ...attributes>
      <label
        data-test-select-label
        for={{@changeset.id}}
        class="{{if this.isDisplayingErrors 'error-description'}} {{if @disabled 'cursor-default'}}"
      >
        {{@label}}
        {{#if @required}}
          <span class="requiredField"></span>
        {{/if}}
        {{#if this.isSensitive}}
          <SensitiveFieldShield />
        {{/if}}
      </label>
      {{#if this.isMasked}}
        <div style="margin-bottom:20px;" class="w-full {{if this.isRevealable 'relative group'}}">
          <input
            type="text"
            value="Hidden"
            disabled
            class="italic !text-carbon-50 {{if this.isRevealable 'pr-8'}}"
            data-test-host-masked
          />
          {{#if this.isRevealable}}
            <SensitiveRevealToggle
              @revealed={{this.isRevealed}}
              @onToggle={{this.toggleRevealed}}
              class="absolute top-[13px] right-2 opacity-0 group-hover:opacity-100"
            />
          {{/if}}
        </div>
      {{else}}
        <div
          style="margin-bottom:20px;"
          class="w-full {{if this.isRevealable 'relative group'}}"
          data-test-option-select
          {{italicizeIfRemoved @selected.name}}
        >
          <PowerSelect
            @allowClear={{@allowClear}}
            @placeholder={{concat "Choose " (firstLowercase @label)}}
            @selected={{this.selected}}
            @disabled={{or @disabled (isRemovedString @selected.name)}}
            @triggerClass={{if this.isDisplayingErrors "power-select-error"}}
            @search={{@search}}
            @searchEnabled={{true}}
            @onChange={{fn @update @changeset}}
            as |employee|
          >
            {{employee.name}}
          </PowerSelect>
          {{#if this.isRevealable}}
            <SensitiveRevealToggle
              @revealed={{this.isRevealed}}
              @onToggle={{this.toggleRevealed}}
              class="absolute top-[13px] {{if @allowClear 'right-12' 'right-8'}} opacity-0 group-hover:opacity-100"
            />
          {{/if}}
        </div>
      {{/if}}
      {{#if this.isDisplayingErrors}}
        <div data-test-input-error class="bottom-error">This is a required field</div>
      {{/if}}
    </div>
  </template>
}
